import { UAParser } from 'ua-parser-js';

import { getAppId } from '@/constants/app';
import { APP_BRANDING_READY_EVENT, type IAppBranding } from '@/constants/app-branding';
import { buildH5CommonParams } from '@/utils/fetch/build-common-params';

import { tracker } from './dual-tracker';

function getBrowserFromUserAgent(): string {
    if (typeof navigator === 'undefined') {
        return '';
    }
    try {
        const { name, version } = new UAParser(navigator.userAgent).getResult().browser;
        const n = name?.trim() ?? '';
        const v = version?.trim() ?? '';
        if (!n) {
            return '';
        }
        return v ? `${n} ${v}` : n;
    } catch {
        return '';
    }
}

let brandingReadyListenerBound = false;

/** app_info 异步就绪后补写 sub_name（首访无缓存时 register 可能早于品牌名） */
function ensureSubPackageProfileSync(): void {
    if (typeof window === 'undefined' || brandingReadyListenerBound) {
        return;
    }
    brandingReadyListenerBound = true;
    window.addEventListener(APP_BRANDING_READY_EVENT, (event: Event) => {
        const { detail } = event as CustomEvent<IAppBranding>;
        const branding = detail ?? window.__H5R_APP_BRANDING__;
        tracker.setUserProfile({
            sub_name: branding?.name ?? '',
        });
    });
}

/**
 * 写入公共埋点字段到内存并在 device_id + user_id 就绪后放行采集（flush 队列 + QT START + 火山 start）。
 * 须在 head BLOCK 之后调用；tracker.start() 幂等。
 */
export const registerTrackerParams = async (params: Record<string, unknown>) => {
    const commonParams = await buildH5CommonParams();
    const deviceId = commonParams.device_id;
    const branding = typeof window !== 'undefined' ? window.__H5R_APP_BRANDING__ : undefined;

    tracker.setUserProfile({
        ...params,
        sub_id: getAppId(),
        sub_name: branding?.name ?? '',
        device_id: deviceId,
        os: commonParams.os_name,
        os_version: commonParams.os_version,
        browser: getBrowserFromUserAgent(),
        language: commonParams.language,
        timezone: commonParams.time_zone,
        country: '',
    });
    if (!branding?.name) {
        ensureSubPackageProfileSync();
    }

    const userId = params.user_id;
    if (userId != null && userId !== '') {
        tracker.setUser({ userId: String(userId) });
    }

    if (deviceId && userId) {
        tracker.start();
    }
};

/**
 * 复访：三级存储已有 device_id 时尽早 setUserProfile + START，无需等 establishSession。
 * @returns 是否已放行采集
 */
export async function tryStartTrackerFromCachedDevice(): Promise<boolean> {
    const commonParams = await buildH5CommonParams();
    if (!commonParams.device_id) {
        return false;
    }
    await registerTrackerParams({});
    return true;
}
